import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getChatSessions } from "../../services/apiService";
import { RootState } from "../store";

// Define the state type
interface ChatHistoryState {
  sessions: any[];
  toggleHistory: boolean;
  loading: boolean;
  error: string | null;
  hasLoaded: boolean;
}

// Define the initial state
const initialState: ChatHistoryState = {
  sessions: [],
  toggleHistory: false,
  loading: false,
  error: null,
  hasLoaded: false,
};

export const fetchChatHistory = createAsyncThunk<
  { sessions: any[]; prepend?: boolean },
  { page: number; limit: number; prepend?: boolean },
  { rejectValue: string; state: RootState }
>(
  "chatHistory/fetchChatHistory",
  async ({ page, limit, prepend }, thunkAPI) => {
    try {
      const sessions = await getChatSessions(page, limit);
      return { sessions, prepend };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message || "Failed to fetch chat history"
      );
    }
  }
);

const chatHistorySlice = createSlice({
  name: "chatHistory",
  initialState,
  reducers: {
    toggleChatHistory: (state) => {
      state.toggleHistory = !state.toggleHistory;
    },
    clearChatHistory: (state) => {
      state.sessions = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchChatHistory.fulfilled,
        (
          state,
          action: PayloadAction<{ sessions: any[]; prepend?: boolean }>
        ) => {
          state.loading = false;
          state.hasLoaded = true;
          const newSessions = action.payload.sessions.filter(
            (newSession) =>
              !state.sessions.some(
                (existingSession) =>
                  existingSession.chat_id === newSession.chat_id
              )
          );
          if (action.payload.prepend) {
            state.sessions = action.payload.sessions;
          } else {
            state.sessions = [...state.sessions, ...newSessions];
          }
        }
      )
      .addCase(fetchChatHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { toggleChatHistory, clearChatHistory } = chatHistorySlice.actions;
export default chatHistorySlice.reducer;
